/**
 * The calendar: the real one (M12, the user's choice).
 *
 * Gregorian, with leap years, so a date in the game is a date in the field
 * record: a Hawaii Ocean Time-series cruise, a satellite's daily sea surface
 * temperature, a tag's track, a survey. It replaced a 360-day year of thirty-
 * day months (Sable Reserve's), which squeezed every dated record onto a
 * shorter year and could not line up with a real year at all; with real
 * dates the game can be run through real years and checked against what was
 * measured on them.
 *
 * Dates are kept as **epoch days**: whole days since 1 January 1970. A world
 * has `startEpochDay`, the date of its day 0, and `day`, the days simulated
 * since; today is their sum (`epochDayOf`). The conversions are Howard
 * Hinnant's `days_from_civil` and `civil_from_days` ("chrono-Compatible
 * Low-Level Date Algorithms"): integer arithmetic, exact over the proleptic
 * Gregorian calendar, and the same in any language. The core may not use
 * `Date` (`tests/coreBoundary.test.ts`); `tests/calendar.test.ts` checks these
 * against it day by day from 1900 to 2100.
 */

export type Hemisphere = 'north' | 'south';
export type Season = 'winter' | 'spring' | 'summer' | 'fall';

/** A date on the calendar. `month` is 1 for January; `day` is the day of the month. */
export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** Days since 1 January 1970 of a calendar date. */
export function epochDayFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const shiftedMonth = (month + 9) % 12; // March is 0, so the leap day ends the year
  const dayOfShiftedYear = Math.floor((153 * shiftedMonth + 2) / 5) + day - 1;
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfShiftedYear;
  return era * 146097 + dayOfEra - 719468;
}

/** The calendar date of an epoch day. */
export function civilFromEpochDay(epochDay: number): CivilDate {
  const z = epochDay + 719468;
  const era = Math.floor(z / 146097);
  const dayOfEra = z - era * 146097;
  const yearOfEra = Math.floor(
    (dayOfEra - Math.floor(dayOfEra / 1460) + Math.floor(dayOfEra / 36524) - Math.floor(dayOfEra / 146096)) / 365,
  );
  const dayOfShiftedYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const shiftedMonth = Math.floor((5 * dayOfShiftedYear + 2) / 153);
  const day = dayOfShiftedYear - Math.floor((153 * shiftedMonth + 2) / 5) + 1;
  const month = shiftedMonth < 10 ? shiftedMonth + 3 : shiftedMonth - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return { year, month, day };
}

/** The Gregorian rule (a definition): every fourth year, but not centuries, but every fourth century. */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

/** From the calendar itself (a definition): the days to the first of the next month. */
export function daysInMonth(year: number, month: number): number {
  return epochDayFromCivil(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1, 1)
    - epochDayFromCivil(year, month, 1);
}

/** A span's days in a year, the span [month, day] to [month, day], both in it: its first, as an epoch day, and how many. */
export function spanIn(span: { from: readonly [number, number]; to: readonly [number, number] }, year: number): { first: number; days: number } {
  const first = epochDayFromCivil(year, span.from[0], span.from[1]);
  return { first, days: epochDayFromCivil(year, span.to[0], span.to[1]) - first + 1 };
}

/** 1 for 1 January, up to 365 or 366. */
export function dayOfYear(epochDay: number): number {
  return epochDay - epochDayFromCivil(civilFromEpochDay(epochDay).year, 1, 1) + 1;
}

/**
 * The day of a 366-day year: 29 February is day 60 and 1 March day 61 in
 * every year, so a day keeps its number in leap and common years alike (the
 * marine heatwave climatology's calendar, Hobday et al. 2016; M24).
 */
export function dayOf366(epochDay: number): number {
  const { year, month } = civilFromEpochDay(epochDay);
  const day = epochDay - epochDayFromCivil(year, 1, 1) + 1;
  return !isLeapYear(year) && month > 2 ? day + 1 : day;
}

/** Today's epoch day in a world. */
export function epochDayOf(world: { startEpochDay: number; day: number }): number {
  return world.startEpochDay + world.day;
}

/** "2 January 2026". */
export function dateLabel(epochDay: number): string {
  const { year, month, day } = civilFromEpochDay(epochDay);
  return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
}

/** "2026-01-02", as the datasets write a date (review M30: the scripts wrote it three times). */
export function isoDate(epochDay: number): string {
  const { year, month, day } = civilFromEpochDay(epochDay);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The season by the hemisphere's calendar: the meteorological seasons,
 * December to February the northern winter and the southern summer.
 *
 * **One season per region, opposite across the equator** (`GDD.md` section 3).
 *
 * Open (M2): the ocean's seasons lag the sun's, because sea surface
 * temperature lags insolation; by the standing rule, Phase 1 takes whichever
 * convention each band's own temperature cycle supports.
 */
export function seasonOf(epochDay: number, hemisphere: Hemisphere): Season {
  const month = civilFromEpochDay(epochDay).month;
  // December, January and February are 0; March to May 1; and so on.
  const quarter = Math.floor((month % 12) / 3);
  return (hemisphere === 'north' ? NORTH : SOUTH)[quarter] as Season;
}

const NORTH: readonly Season[] = ['winter', 'spring', 'summer', 'fall'];
const SOUTH: readonly Season[] = ['summer', 'fall', 'winter', 'spring'];
